import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { User, LoginApiResponse } from '../models/user.model';
import { Http } from './http';

export type { User };

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'currentUser';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public currentUser$ = new BehaviorSubject<User | null>(null);
  public isAuthenticated$ = new BehaviorSubject<boolean>(false);

  readonly currentUser = this.currentUser$.asObservable();
  readonly isAuthenticated = this.isAuthenticated$.asObservable();

  // ── Mock user data (commented out — replaced by real API) ────────────────
  // private mockUsers: { [key: string]: { password: string; user: User } } = {
  //   admin: {
  //     password: 'admin123',
  //     user: { id: '1', name: 'Admin User', email: 'admin@AskLab.com', role: 'admin', avatar: '👤' },
  //   },
  //   user: {
  //     password: 'user123',
  //     user: { id: '2', name: 'John Analyst', email: 'john@AskLab.com', role: 'user', avatar: '👤' },
  //   },
  // };

  constructor(private http: Http) {
    this.loadStoredUser();
  }

  // ── Token helpers ─────────────────────────────────────────────────────────

  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return this.isAuthenticated$.value && !!this.currentUser$.value;
  }

  // ── Auth actions ──────────────────────────────────────────────────────────

  /**
   * Real API login:
   * 1. POST /auth/login → get { accessToken, refreshToken, ... }
   * 2. Decode JWT to extract userId
   * 3. GET /users/:userId → get full user profile
   * 4. Map isAdmin → role for UI compatibility
   */
  login(email: string, password: string): Observable<boolean> {
    return this.http.post<LoginApiResponse>('/auth/login', { email, password }).pipe(
      switchMap((response) => {
        const { accessToken, refreshToken } = response.result;
        this.setTokens(accessToken, refreshToken);

        // Decode JWT payload to extract userId (no signature verification needed on client)
        const payload = this.decodeJwtPayload(accessToken);
        const userId = payload?.userId;

        if (!userId) {
          return of(false);
        }

        // Fetch full user profile using userId from token
        return this.fetchAndStoreUserProfile(userId);
      }),
      catchError(() => of(false)),
    );
  }

  // ── Mock login (commented out — replaced by real API above) ──────────────
  // login(email: string, password: string): Observable<boolean> {
  //   return new Observable((observer) => {
  //     setTimeout(() => {
  //       const userEntry = Object.values(this.mockUsers).find(
  //         (entry) => entry.user.email === email && entry.password === password,
  //       );
  //       if (userEntry) {
  //         this.currentUser$.next(userEntry.user);
  //         this.isAuthenticated$.next(true);
  //         localStorage.setItem(USER_KEY, JSON.stringify(userEntry.user));
  //         // Mock tokens — real tokens come from API response on integration
  //         this.setTokens('mock-access-token', 'mock-refresh-token');
  //         observer.next(true);
  //       } else {
  //         observer.next(false);
  //       }
  //       observer.complete();
  //     }, 500);
  //   });
  // }

  /**
   * Logout: revoke refresh token on server, then clear local session.
   * Fire-and-forget for the API call — always clears local state.
   */
  logout(): void {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      // Revoke token on server (fire and forget — don't block local logout)
      this.http.post<void>('/auth/logout', { refreshToken }).subscribe({
        error: () => { /* ignore server errors on logout */ }
      });
    }
    this.currentUser$.next(null);
    this.isAuthenticated$.next(false);
    localStorage.removeItem(USER_KEY);
    this.clearTokens();
  }

  getCurrentUser(): User | null {
    return this.currentUser$.value;
  }

  isAdmin(): boolean {
    return this.currentUser$.value?.role === 'admin';
  }

  // ── Session restore ───────────────────────────────────────────────────────

  private loadStoredUser(): void {
    const storedUser = localStorage.getItem(USER_KEY);
    const token = this.getAccessToken();
    if (storedUser && token) {
      try {
        const user: User = JSON.parse(storedUser);
        this.currentUser$.next(user);
        this.isAuthenticated$.next(true);
      } catch {
        this.logout();
      }
    }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /**
   * GET /users/:userId — fetch user profile and store in state.
   * Backend already returns role ('admin'|'user') directly.
   */
  private fetchAndStoreUserProfile(userId: string): Observable<boolean> {
    return this.http.get<User>(`/users/${userId}`).pipe(
      map((response) => {
        const userData = response.result;
        const user: User = {
          ...userData,
          // Use backend role directly; derive isAdmin as boolean for type safety
          role: userData.role ?? (userData.isAdmin ? 'admin' : 'user'),
          isAdmin: !!userData.isAdmin,
          isActive: !!userData.isActive,
        };
        this.currentUser$.next(user);
        this.isAuthenticated$.next(true);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  /**
   * Decode JWT payload without verification (client-side only).
   * Backend JWT contains: { userId, iat, exp }
   */
  private decodeJwtPayload(token: string): { userId?: string } | null {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }
}

